import Column from '@components/Column';
import Image from '@components/Image';
import Row from '@components/Row';
import Text from '@components/Text';
import colors from '@constants/colors';
import styled from '@emotion/styled';
import usePriceUnit from '@hooks/item/usePriceUnit';
import { TierAccumulationType, TierBenefitTierRow } from '@models/tier';
import { Trans, useTranslation } from 'react-i18next';

interface Props {
    tier: TierBenefitTierRow;
    period?: string;
    accumulationType: TierAccumulationType,
}

function BenefitTierItem({ tier, period, accumulationType }:Props) {
    const { t } = useTranslation('tier');
    const {
        tierName, iconFileInfo, displayType, backgroundColor, textColor,
        tierThreshold, benefits,
    } = tier;
    const priceUnit = usePriceUnit();

    const isStamp = accumulationType === 'STAMP';
    const threshold = isStamp
        ? tierThreshold.toLocaleString()
        : `${tierThreshold.toLocaleString()}${priceUnit}`;
    const thresholdKey = isStamp ? 'tierBenefits.thresholdStamp' : 'tierBenefits.thresholdPayment';

    return (
        <Column fullWidth spacing={2}>
            <Row fullWidth justify='space-between'>
                {displayType === 'ICON' ? (
                    <Row spacing={2}>
                        <Image src={iconFileInfo?.url}
                            width={24}
                            height={24} />
                        <Text fontWeight='bold'>{tierName}</Text>
                    </Row>
                ) : (
                    <TierBadge backgroundColor={backgroundColor}
                        textColor={textColor} >
                        {tierName}
                    </TierBadge>
                )}
                <Text size='body-long' fontWeight='medium' color={colors.gray6}>
                    <Trans ns='tier'
                        i18nKey={thresholdKey}
                        values={{ period, threshold }}
                        components={{ bold: <Text size='body-long' fontWeight='bold' as='span' /> }}
                    />
                </Text>
            </Row>
            {benefits.map((benefit) => (
                <BeneFit key={benefit.benefitId}>
                    <Text fontWeight='medium'>{benefit.couponName}</Text>
                    <Text color={colors.textThird}>
                        {t('tierBenefits.validityDays', { days: benefit.validityDays })}
                    </Text>
                </BeneFit>
            ))}
        </Column>
    );
}

const TierBadge = styled.div<{
    backgroundColor?: string;
    textColor?: string;
}>`
    padding: 4px 12px;
    border-radius: 16px;
    font-size: 14px;
    font-weight: bold;
    background-color: ${({ backgroundColor }) => backgroundColor};
    color: ${({ textColor }) => textColor};
`;

const BeneFit = styled.div`
    padding: 12px 16px;
    background-color: var(--color-gray1);
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    border-radius: 8px;
`;

export default BenefitTierItem;
